import {create} from 'zustand'
import {devtools, persist} from 'zustand/middleware'

/** 기록 홈 카드 하나가 쓰는 값 */
export type PlanInfo = {
  title: string,
  target: number,
  kcal: number,
  unitType: string,
  infoText: string,
  navi: () => void,
}

type Store = {
  exerciseInfo: PlanInfo,
  mealInfo: PlanInfo,
  stepInfo: PlanInfo,
  moistureInfo: PlanInfo,
  selectedFoodList: []
  setSelectedFoodList: (by: []) => void
  selectedExerciseList: []
  setSelectedExerciseList: (by: []) => void
  selectedDeviceList: string[]
  setSelectedDeviceList: (by: string[]) => void
  /**
   * selectedDeviceList 를 저장한 계정(localStorage.userID).
   *
   * 기기 선택은 AP_009_003 으로 **계정별로** 서버에 저장된다. 그런데 이 목록은
   * 로그아웃 후에도 유지되는 값이라(util.clearLoginStorage 참고), 소유자를 함께
   * 적어두지 않으면 다른 계정으로 로그인했을 때 이전 계정의 선택이 그대로 보이고
   * 그 상태에서 저장하면 새 계정의 서버 등록을 덮어쓴다.
   * 읽을 때는 selectedDevicesOf 로 소유자를 확인한다.
   */
  selectedDeviceOwner: string
}

/** 기기 선택을 저장·조회하는 주체 */
const accountKey = () => window.localStorage.getItem('userID') ?? '';

const useStore = create<Store>()(
  devtools(
    persist(
      (set) => ({
        // 아래 4개 세터는 상태를 직접 고치지 않고 새 객체를 만든다.
        // 예전에는 `state.exerciseInfo[name] = value; return {}` 였다. zustand 가
        // 매번 새 최상위 객체를 만들어 주는 덕에 전체 구독(useStore())에서는 우연히
        // 화면이 갱신됐지만, 셀렉터(useStore(s => s.exerciseInfo))를 쓰는 순간
        // 참조가 그대로라 갱신되지 않는다.
        exerciseInfo: {title: '', target: 0, kcal: 0, unitType: '', infoText: '', navi: () => {}},
        setExerciseInfo: (name: any, value: any) => set((state: any) => ({
          exerciseInfo: { ...state.exerciseInfo, [name]: value },
        })),
        mealInfo: {title: '', target: 0, kcal: 0, unitType: '', infoText: '', navi: () => {}},
        setMealInfo: (name: any, value: any) => set((state: any) => ({
          mealInfo: { ...state.mealInfo, [name]: value },
        })),
        stepInfo: {title: '', target: 0, kcal: 0, unitType: '', infoText: '', navi: () => {}},
        setStepInfo: (name: any, value: any) => set((state: any) => ({
          stepInfo: { ...state.stepInfo, [name]: value },
        })),
        moistureInfo: {title: '', target: 0, kcal: 0, unitType: '', infoText: '', navi: () => {}},
        setMoistureInfo: (name: any, value: any) => set((state: any) => ({
          moistureInfo: { ...state.moistureInfo, [name]: value },
        })),
        selectedFoodList: [],
        setSelectedFoodList: (by: []) => set(() => ({ selectedFoodList: by })),
        selectedExerciseList: [],
        setSelectedExerciseList: (by: []) => set(() => ({ selectedExerciseList: by })),
        selectedDeviceList: [],
        selectedDeviceOwner: '',
        // 저장할 때 어느 계정의 선택인지 함께 남긴다
        setSelectedDeviceList: (by: string[]) => set(() => ({
          selectedDeviceList: by,
          selectedDeviceOwner: accountKey(),
        })),
      }),
      { name: 'historyStore' },
    ),
  ),
)

/**
 * 지금 로그인한 계정이 고른 기기 목록.
 *
 * 저장해 둔 소유자와 다르면 빈 배열을 준다 — 이전 계정의 선택이 넘어오지 않게 한다.
 * 같은 계정으로 다시 로그인하면 그대로 남는다(로그아웃마다 다시 고르게 하지 않기 위함).
 * 비로그인 상태에서도 빈 배열이다.
 */
export const selectedDevicesOf = (state: {
  selectedDeviceList?: string[],
  selectedDeviceOwner?: string,
}): string[] => {
  const owner = accountKey();
  if (owner === '') return [];
  if (state.selectedDeviceOwner !== owner) return [];
  return state.selectedDeviceList ?? [];
};

export default useStore;
